import React from 'react'
import { isNull } from 'lodash'
import {
  InsuranceCompanies_getInsuranceCompanies_insuranceCompanies,
  TestSessions_testSessions_pagination,
  SortDirectionEnum,
} from '@modmd/data'
import { Box } from 'components/Layout'
import Datalist, { DataListProps } from 'components/Datalist'
import Pagination from 'components/Pagination'
import Card from 'components/Card'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import { DEVICE } from 'theme'
import Label from 'components/Label'

interface ColumnLabels {
  date?: string | null
  time?: string | null
  location?: string | null
  numOfPeople?: string | null
  company?: string | null
  group?: string | null
  status?: string | null
  paymentStatus?: string | null
  invoiceCheck?: string | null
}

interface Props {
  tableHeader?: React.ReactElement
  tableFooter?: React.ReactElement
  insuranceCompanies: InsuranceCompanies_getInsuranceCompanies_insuranceCompanies[]
  sorter?: DataListProps['sorter']
  columnLabels?: ColumnLabels
  pagination?: TestSessions_testSessions_pagination
  isCard?: boolean
  isLoading?: boolean
  hasPaginationInFooter?: boolean
  onPageChange?: (page: number) => void
  onSortChange?: (sortBy: string, direction: SortDirectionEnum) => void
  onRowClick: (data: InsuranceCompanies_getInsuranceCompanies_insuranceCompanies) => void
  pageLength?: number
  listOfInvoices?: Array<string>
  setListOfInvoices?: React.Dispatch<React.SetStateAction<Array<string>>>
  refetch: () => void
}

export const PAGE_LENGTH = 10

const defaultColumnLabels = {
  name: 'Insurance Company name',
  daysBetweenTests: 'Days between tests',
  pVerifyId: 'pVerify ID',
  isApproved: 'Approved',
}

export const InsuranceCompaniesDatalist: React.VFC<Props> = ({
  tableHeader,
  tableFooter,
  insuranceCompanies,
  sorter,
  pagination,
  columnLabels: providedColumnLabels,
  isCard = true,
  isLoading,
  hasPaginationInFooter = false,
  onSortChange,
  onPageChange,
  onRowClick,
  pageLength = PAGE_LENGTH,
}) => {
  const isDesktop = useIsMinDevice(DEVICE.DESKTOP)
  const columnLabels = { ...defaultColumnLabels, ...(providedColumnLabels || {}) }
  const numOfColumns = Object.values(columnLabels).filter((label) => label !== null).length
  const paginationComp = pagination && pagination.total > pageLength && onPageChange && (
    <Pagination
      firstPage={1}
      lastPage={Math.ceil(pagination.total / pageLength) || 1}
      currentPage={Math.ceil(pagination.from / pageLength) + 1}
      onPageChange={onPageChange}
    />
  )

  const Wrapper = isCard ? Card : Box
  return (
    <>
      <Wrapper>
        <Datalist
          isLoading={isLoading}
          skeletonProps={{
            columns: numOfColumns,
            rows: pageLength,
          }}
          data={insuranceCompanies}
          sorter={sorter}
          onSortChange={onSortChange}
          header={tableHeader}
          footer={hasPaginationInFooter && paginationComp ? paginationComp : tableFooter}
          shouldRenderTableHeader={isDesktop}
          showActionIndicators
          renderTableHeader={
            isDesktop
              ? () => (
                  <Datalist.Row gridTemplateColumns="2fr 1fr 1fr 1fr">
                    {!isNull(columnLabels.name) ? (
                      <Datalist.HeaderCell label={columnLabels.name} />
                    ) : null}
                    {!isNull(columnLabels.daysBetweenTests) ? (
                      <Datalist.HeaderCell label={columnLabels.daysBetweenTests} />
                    ) : null}
                    {!isNull(columnLabels.pVerifyId) ? (
                      <Datalist.HeaderCell label={columnLabels.pVerifyId} />
                    ) : null}
                    {!isNull(columnLabels.isApproved) ? (
                      <Datalist.HeaderCell label={columnLabels.isApproved} />
                    ) : null}
                  </Datalist.Row>
                )
              : undefined
          }
          renderTableRow={(data) => {
            const { isApproved, daysBetweenTests, name, pVerifyId } = data
            return (
              <Datalist.Row
                onClick={
                  onRowClick
                    ? () => {
                        onRowClick(data)
                      }
                    : undefined
                }
                gridTemplateColumns="2fr 1fr 1fr 1fr"
                {...(!isDesktop ? { alignItems: 'start' } : {})}
              >
                {!isNull(columnLabels.name) ? (
                  <Datalist.Cell label={columnLabels.name}>{name}</Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.name) ? (
                  <Datalist.Cell label={columnLabels.daysBetweenTests}>
                    {daysBetweenTests}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.name) ? (
                  <Datalist.Cell label={columnLabels.pVerifyId}>{pVerifyId}</Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.isApproved) ? (
                  <Datalist.Cell label={columnLabels.isApproved}>
                    <Label indicator={isApproved ? `green` : 'red'}>
                      {isApproved ? `Approved` : 'Not Approved'}
                    </Label>
                  </Datalist.Cell>
                ) : null}
              </Datalist.Row>
            )
          }}
        />
      </Wrapper>
      {!hasPaginationInFooter && paginationComp && (
        <Pagination.Wrapper>{paginationComp}</Pagination.Wrapper>
      )}
    </>
  )
}
